/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, NavLink, Outlet, useLocation } from "react-router-dom";
import type { RootState } from "../../app/store";
import "./Dashboard.css";
import { AppDispatch } from "../../app/store";
import { logout } from "../../features/auth/authSlice";

const MOBILE_BREAKPOINT = "(max-width: 768px)";

function isMobileViewport() {
  return typeof window !== "undefined" && window.matchMedia(MOBILE_BREAKPOINT).matches;
}


const IconDashboard = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

const IconProjects = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M3 7V5a2 2 0 0 1 2-2h5l2 2h9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
  </svg>
);

const IconPartners = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const IconProfile = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const IconLogout = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);
const IconBell = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const Dashboard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { role, displayName, partnerId } = useSelector((state: RootState) => state.auth);
  const { notifications, unreadCount } = useSelector((state: RootState) => state.notifications);

  const isAdmin = role === "admin";
  const [sidebarOpen, setSidebarOpen] = useState(() => !isMobileViewport());

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_BREAKPOINT);
    const onChange = (event: MediaQueryListEvent) => {
      setSidebarOpen(!event.matches);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Close the mobile drawer after navigating to a page.
  useEffect(() => {
    if (isMobileViewport()) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };
  // console.log("show unread count from sidebar0----->", unreadCount);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  return (
    <div className={`layout ${sidebarOpen ? "sidebar-open" : "sidebar-collapsed"}`}>
      <aside className={`sidebar ${sidebarOpen ? "open" : "collapsed"}`}>
        <div className="sidebar-header">
          <button
            type="button"
            className="sidebar-toggle"
            onClick={toggleSidebar}
            aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
          >
            <span className={`sidebar-toggle__icon ${sidebarOpen ? "open" : ""}`}>
              <span />
              <span />
              <span />
            </span>
          </button>

          {sidebarOpen && (
            <img
              src="/assets/images/Trim_logo.png"
              alt="Logo"
              className="logo"
            />
          )}

        </div>

        <nav className="menu">
          <NavLink
            to="/dashboard"
            end
            title="Dashboard"
            className={({ isActive }) =>
              `menu-item ${isActive ? "active" : ""}`
            }
          >
            <span className="menu-item__icon"><IconDashboard /></span>
            <span className="menu-item__label">Dashboard</span>
          </NavLink>
          <NavLink
            to="/dashboard/create-project"
            title="Projects"
            className={({ isActive }) =>
              `menu-item ${isActive ? "active" : ""}`
            }
          >
            <span className="menu-item__icon"><IconProjects /></span>
            <span className="menu-item__label">Projects</span>
          </NavLink>
          {isAdmin && (
            <NavLink
              to="/dashboard/partners"
              title="Partners"
              className={({ isActive }) =>
                `menu-item ${isActive ? "active" : ""}`
              }
            >
              <span className="menu-item__icon"><IconPartners /></span>
              <span className="menu-item__label">Partners</span>
            </NavLink>
          )}
          <NavLink
            to="/dashboard/profile"
            title="Profile"
            className={({ isActive }) =>
              `menu-item ${isActive ? "active" : ""}`
            }
          >
            <span className="menu-item__icon"><IconProfile /></span>
            <span className="menu-item__label">Profile</span>
          </NavLink>

          {role === "partner" && (   
          <NavLink
            to="/dashboard/notifications"
            title="notification"
            className={({ isActive }) => `menu-item ${isActive ? "active" : ""}`}
          >
            <span className="menu-item__icon"><IconBell />
            {unreadCount > 0 && (
      <span className="menu-item__badge">
        {unreadCount > 99 ? '99+' : unreadCount}
      </span>
    )}
            </span>
            <span className="menu-item__label">Notifications</span>
          </NavLink>
)}
        </nav>

        {sidebarOpen && displayName && role === "partner" && (
          <p style={{ padding: "0 1rem", fontSize: "0.8rem", color: "#9b8fb5" }}>
            Signed in as {displayName}
          </p>
        )}



        <button type="button" className="logout-btn" onClick={handleLogout} title="Logout">
          <span className="logout-btn__icon logout-btn__icon--bell"><IconLogout /></span>
          <span className="logout-btn__label">Logout</span>
        </button>
        {/* {partnerId && (

           <main className="main"> 
          
           

            <TopBar title=""/>                            
       <Outlet />
          

          </main> 
          )} */}



      </aside>

      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}


      <main className="main">
        <div className="mobile-topbar">
          <button
            type="button"
            className="sidebar-toggle"
            onClick={toggleSidebar}
            aria-label={sidebarOpen ? "Close menu" : "Open menu"}
          >
            <span className={`sidebar-toggle__icon ${sidebarOpen ? "open" : ""}`}>
              <span />
              <span />
              <span />
            </span>
          </button>
          <img
            src="/assets/images/Trim_logo.png"
            alt="Logo"
            className="mobile-topbar__logo"
          />
        </div>
        <div className="main-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
